import { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  exportCloud,
  exportDecisionsCsv,
  exportLocalJson,
  exportMealsCsv,
  exportMeasurementsCsv,
  exportSetsCsv,
  cloudExportAvailable,
} from "../../lib/export";
import { Button, Card, SectionTitle, Sheet } from "../../components/ui";

export function ExportSection() {
  const [confirmLocal, setConfirmLocal] = useState(false);
  const [cloudOpen, setCloudOpen] = useState(false);
  const [cloudPassword, setCloudPassword] = useState("");
  const [cloudNote, setCloudNote] = useState<string | null>(null);
  const [localDone, setLocalDone] = useState(false);

  const doExport = async (kind: "json" | "sets" | "meals" | "measurements" | "decisions") => {
    switch (kind) {
      case "json":
        await exportLocalJson();
        break;
      case "sets":
        await exportSetsCsv();
        break;
      case "meals":
        await exportMealsCsv();
        break;
      case "measurements":
        await exportMeasurementsCsv();
        break;
      case "decisions":
        await exportDecisionsCsv();
        break;
    }
    setLocalDone(true);
  };

  const doCloud = async () => {
    setCloudNote("正在重新验证并导出…");
    const r = await exportCloud(cloudPassword, "json");
    setCloudNote(r.message);
    if (r.ok) setCloudOpen(false);
  };

  return (
    <>
      <SectionTitle>本地与云端备份（§28）</SectionTitle>
      <Card className="p-4 mb-4 space-y-3.5 border border-tone-border shadow-sm">
        <p className="text-[12px] text-tone-soft leading-relaxed">
          导出内容包含规则版本与单位口径（kg/只、总重、器械显示值等，不做换算）。
          <strong>本地导出仅包含本设备当前数据</strong>；云端导出需重新输入密码验证身份（部署后可用）。
        </p>
        <div className="grid grid-cols-2 gap-2">
          <Button variant="secondary" onClick={() => setConfirmLocal(true)}>
            JSON 完整备份
          </Button>
          <Button variant="secondary" onClick={() => void doExport("sets")}>
            CSV 训练组记录
          </Button>
          <Button variant="secondary" onClick={() => void doExport("meals")}>
            CSV 饮食记录
          </Button>
          <Button variant="secondary" onClick={() => void doExport("measurements")}>
            CSV 身体数据
          </Button>
          <Button variant="secondary" onClick={() => void doExport("decisions")}>
            CSV 进阶决策
          </Button>
          <Button
            variant="ghost"
            onClick={() =>
              void cloudExportAvailable().then((c) => {
                setCloudNote(c.reason ?? "可用");
                setCloudOpen(c.available);
              })
            }
          >
            云端导出（重验）
          </Button>
        </div>
        {localDone ? <p className="text-[12px] text-tone-good font-semibold">已下载 ✓（本地导出仅含本设备数据）</p> : null}
        {cloudOpen ? (
          <div className="rounded-xl border border-tone-border p-3 space-y-2 bg-black/[0.02] dark:bg-white/[0.02]">
            <p className="text-[12px] text-tone-soft">
              重新输入密码以授权云端导出（ADR-011；密码仅用于本次验证，不保存）。
            </p>
            <input
              type="password"
              value={cloudPassword}
              onChange={(e) => setCloudPassword(e.target.value)}
              placeholder="密码"
              className="w-full min-h-[44px] px-4 rounded-xl bg-tone-card border border-tone-border text-tone-ink text-sm"
            />
            <Button className="w-full" disabled={!cloudPassword} onClick={() => void doCloud()}>
              确认并导出云端数据
            </Button>
          </div>
        ) : null}
        {cloudNote ? <p className="text-[12px] text-tone-soft">{cloudNote}</p> : null}
      </Card>

      <Sheet open={confirmLocal} onClose={() => setConfirmLocal(false)} title="确认本地导出">
        <div className="p-4 space-y-3">
          <p className="text-[13px] text-tone-ink leading-relaxed">
            你将导出<b>本设备</b>的完整数据（JSON 备份，含训练/饮食/体重/决策），
            其中可能包含尚未同步到云端的内容。导出文件请自行妥善保管。
          </p>
          <p className="text-[12px] text-tone-soft mt-1">确认后开始导出；系统不会删除任何数据。</p>
          <div className="grid grid-cols-2 gap-2 pt-2">
            <Button
              className="w-full"
              onClick={() => {
                void doExport("json");
                setConfirmLocal(false);
              }}
            >
              确认导出
            </Button>
            <Button variant="ghost" className="w-full" onClick={() => setConfirmLocal(false)}>
              取消
            </Button>
          </div>
        </div>
      </Sheet>
    </>
  );
}

export function SettingsExport() {
  const nav = useNavigate();

  return (
    <div className="min-h-screen mx-auto max-w-[480px] px-4 pt-safe-top pb-10">
      {/* 顶部返回导航栏 */}
      <div className="flex items-center justify-between py-3 mb-2">
        <button
          onClick={() => nav(-1)}
          className="inline-flex items-center gap-1 text-tone-accent font-semibold text-sm active:opacity-70"
        >
          <svg viewBox="0 0 24 24" className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth={2.2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
          </svg>
          返回
        </button>
        <h1 className="text-[17px] font-bold text-tone-ink">数据导出</h1>
        <div className="w-12" />
      </div>

      <ExportSection />
    </div>
  );
}
